import React, { useMemo } from 'react'
import { getClassNames } from '../../_utils/classnames'
import ColorPickerPopover from '../color-picker-popover/color-picker-popover'

import colorConvert from 'color-convert'
import './templatized-text-field-with-color.scss'

import { BgColorsOutlined, FontColorsOutlined } from '@ant-design/icons'
import TemplatizedTextField, {
    InputFocusRef,
    TemplatizedTextFieldProps,
} from '@pushly/aqe/lib/components/templatized-text-field/templatized-text-field'
import { isNullOrUndefined } from '@pushly/aqe/lib/utils'
import { Button, Tooltip } from 'antd'

export type TextWithForegroundColorValue = {
    value?: string | null
    fgColor?: string | null
}

export type TextWithColorValues = TextWithForegroundColorValue & {
    bgColor?: string | null
}

type PropsWithForegroundColor = {
    showBgColorOption?: false

    value?: TextWithForegroundColorValue
    initialValue?: TextWithForegroundColorValue

    fgColorIcon?: React.ReactNode
    fgColorTitle?: string | React.ReactNode

    onChange?: (value: TextWithForegroundColorValue) => void
}

type PropsWithColors = {
    showBgColorOption: true

    value?: TextWithColorValues
    initialValue?: TextWithColorValues

    fgColorIcon?: React.ReactNode
    fgColorTitle?: string | React.ReactNode
    bgColorIcon?: React.ReactNode
    bgColorTitle?: string | React.ReactNode

    onChange?: (value: TextWithColorValues) => void
}

type TTFWCProps<ValueType extends TextWithColorValues> = Omit<TemplatizedTextFieldProps, 'value' | 'onChange'> & {
    ref?: React.Ref<InputFocusRef>

    value?: ValueType
    initialValue?: ValueType
    onChange?: (value: ValueType) => void

    /** @deprected use `value` */
    fieldValue?: string
    /** @deprected use `onChange` */
    onFieldChange?: (field: string) => void
    /** @deprected use `value` */
    colorValue?: string
    /** @deprected use `onChange` */
    onColorChange?: (color: string) => void
    /** @deprected use `fgColorIcon` */
    colorIcon?: React.ReactNode
    /** @deprected use `fgColorTitle` */
    colorTitle?: string | React.ReactNode
    /** @deprecated use `initialValue` */
    defaultValue?: string | null
} & (PropsWithForegroundColor | PropsWithColors)

const DEFAULT_FG_COLOR_VALUE = '#ffffff'
const DEFAULT_BG_COLOR_VALUE = '#222222'

export const TemplatizedTextFieldWithColor = <ValueType extends TextWithColorValues = any>(
    props: TTFWCProps<ValueType>,
) => {
    let initialValue = props.initialValue ?? props.defaultValue

    let textInitialValue: string | null | undefined
    let fgColorInitialValue: string | null | undefined = DEFAULT_FG_COLOR_VALUE
    let bgColorInitialValue: string | null | undefined = DEFAULT_BG_COLOR_VALUE
    if (initialValue) {
        if (typeof initialValue === 'string') {
            textInitialValue = initialValue
        } else {
            textInitialValue = initialValue.value
            fgColorInitialValue = initialValue.fgColor ?? DEFAULT_FG_COLOR_VALUE
            bgColorInitialValue = props.showBgColorOption
                ? props.initialValue?.bgColor ?? DEFAULT_BG_COLOR_VALUE
                : undefined
        }
    }

    let propsTextValue: string | null | undefined = props.fieldValue
    let propsFgColorValue: string | null | undefined = props.colorValue
    let propsBgColorValue: string | null | undefined
    if (props.value) {
        propsTextValue = props.value.value
        propsFgColorValue = props.value.fgColor
        propsBgColorValue = props.showBgColorOption ? props.value.bgColor : undefined
    }

    const [textValue, setTextValue] = React.useState<string | null | undefined>(propsTextValue ?? textInitialValue)
    const [fgColorValue, setFgColorValue] = React.useState<string | null | undefined>(
        propsFgColorValue ?? fgColorInitialValue,
    )
    const [bgColorValue, setBgColorValue] = React.useState<string | null | undefined>(
        propsBgColorValue ?? bgColorInitialValue,
    )

    // Handle managed value mode
    React.useEffect(() => {
        if (props.value) {
            if (!isNullOrUndefined(props.value.value) && props.value.value !== textValue) {
                setTextValue(props.value.value)
            }
            if (!isNullOrUndefined(props.value.fgColor) && props.value.fgColor !== fgColorValue) {
                setFgColorValue(props.value.fgColor)
            }
            if (!isNullOrUndefined(props.value.bgColor) && props.value.bgColor !== bgColorValue) {
                setBgColorValue(props.value.bgColor)
            }
        } else {
            if (!isNullOrUndefined(props.fieldValue) && props.fieldValue !== textValue) {
                setTextValue(props.fieldValue)
            }
            if (!isNullOrUndefined(props.colorValue) && props.colorValue !== fgColorValue) {
                setFgColorValue(props.colorValue)
            }
        }
    }, [props.fieldValue, props.colorValue, props.value?.value, props.value?.fgColor, props.value?.bgColor])

    const handleFieldChange = (v: string) => {
        if (v !== textValue) {
            setTextValue(v)
            props.onFieldChange?.(v)
            handleChange({ value: v } as Partial<ValueType>)
        }
    }

    const handleFgColorChange = (v: string) => {
        if (v !== fgColorValue) {
            setFgColorValue(v)
            props.onColorChange?.(v)
            handleChange({ fgColor: v } as Partial<ValueType>)
        }
    }

    const handleBgColorChange = (v: string) => {
        if (v !== bgColorValue) {
            setBgColorValue(v)
            handleChange({ bgColor: v } as Partial<ValueType>)
        }
    }

    const handleChange = (update: Partial<ValueType>) => {
        props.onChange?.({
            value: textValue,
            fgColor: fgColorValue,
            bgColor: bgColorValue,
            ...update,
        })
    }

    const fgColorIcon = useMemo(() => {
        return props.fgColorIcon ?? props.colorIcon ?? <FontColorsOutlined />
    }, [props.fgColorIcon, props.colorIcon])

    const bgColorIcon = useMemo(() => {
        return (props.showBgColorOption ? props.bgColorIcon ?? props.bgColorIcon : null) ?? <BgColorsOutlined />
    }, [props.showBgColorOption, (props as PropsWithColors).bgColorIcon])

    return (
        <>
            <TemplatizedTextField
                {...props}
                ref={props.ref}
                className={getClassNames(null, props.className, 'color-field', 'ttfwc-value-field', {
                    ['with-bg-color']: props.showBgColorOption,
                })}
                value={textValue}
                onChange={handleFieldChange}
                addon={
                    <>
                        <ColorPickerPopover
                            className="color-popover-btn ttfwc-fg-color-field"
                            content={
                                <Tooltip trigger="hover" title={props.fgColorTitle ?? props.colorTitle ?? 'Font Color'}>
                                    <Button
                                        disabled={props.disabled}
                                        style={{
                                            color:
                                                colorConvert.hex.hsl(fgColorValue ?? DEFAULT_FG_COLOR_VALUE)?.[2] < 70
                                                    ? 'white'
                                                    : 'black' ?? 'black',
                                        }}
                                    >
                                        <span
                                            className="color-wrap"
                                            style={{
                                                backgroundColor: fgColorValue ?? DEFAULT_FG_COLOR_VALUE,
                                            }}
                                        >
                                            {fgColorIcon}
                                        </span>
                                    </Button>
                                </Tooltip>
                            }
                            defaultColor={fgColorInitialValue}
                            color={fgColorValue}
                            onChange={handleFgColorChange}
                            disabled={props.disabled}
                        />
                        {props.showBgColorOption && (
                            <ColorPickerPopover
                                className="bg-color-popover-btn ttfwc-bg-color-field"
                                content={
                                    <Tooltip trigger="hover" title={props.bgColorTitle ?? 'Background Color'}>
                                        <Button
                                            disabled={props.disabled}
                                            style={{
                                                color:
                                                    colorConvert.hex.hsl(bgColorValue ?? DEFAULT_BG_COLOR_VALUE)?.[2] <
                                                    70
                                                        ? 'white'
                                                        : 'black' ?? 'black',
                                            }}
                                        >
                                            <span
                                                className="color-wrap"
                                                style={{
                                                    backgroundColor: bgColorValue ?? DEFAULT_BG_COLOR_VALUE,
                                                }}
                                            >
                                                {bgColorIcon}
                                            </span>
                                        </Button>
                                    </Tooltip>
                                }
                                defaultColor={bgColorInitialValue}
                                color={bgColorValue}
                                onChange={handleBgColorChange}
                                disabled={props.disabled}
                            />
                        )}
                    </>
                }
            />
        </>
    )
}
